import {
  addItemToCollection,
  getItemFromCollection,
  updateItemInCollection,
} from "directus/src/operations";
import { useMutation } from "@tanstack/react-query";

import { getRequiredFields } from "components/form/utils";
import { getMilestoneSchema } from "./schema";
import { useForm } from "react-hook-form";
import { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { queryClient } from "utils/react-query";
import { toast } from "sonner";

export const useMilestoneForm = ({ defaultJobId, type }) => {
  const { milestoneId } = useParams();

  const [status, setStatus] = useState("in_progress");
  const [apiStatus, setApiStatus] = useState(null);

  const schema = useMemo(() => getMilestoneSchema(type, status), [type, status]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      status: "in_progress",
    },
  });

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "status" && value?.status !== status)
        setStatus(value.status);
    });
    return () => subscription.unsubscribe();
  }, [form, status]);

  const requiredFields = useMemo(
    () => getRequiredFields(schema),
    [schema],
  );

  const createMutation = useMutation({
    mutationFn: (values) => addItemToCollection("milestones", values),

    onSuccess: () => {
      toast.success("Milestone created successfully");
      queryClient.invalidateQueries(["milestones"]);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ milestoneId, values }) => {
      await updateItemInCollection("milestones", milestoneId, values);
    },

    onSuccess: () => {
      toast.success("Milestone updated successfully");
      queryClient.invalidateQueries(["milestones"]);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleCreate = async (context) => {
    const values = { ...context.values, job: defaultJobId };
    return await createMutation.mutateAsync(values);
  };

  const handleUpdate = async (milestoneId, context) => {
    const values = context.dirtyValues;

    let cleanedValues = values;
    if (values.coordinator) {
      cleanedValues.coordinator = values.coordinator.id;
    }

    await updateMutation.mutateAsync({ milestoneId, values: cleanedValues });
    if (cleanedValues.status) {
      setApiStatus(cleanedValues.status);
    }
  };

  const handleClose = async () => {
    await updateMutation.mutateAsync({
      milestoneId,
      values: { status: "cancelled" },
    });
    setApiStatus("cancelled");
  };

  const handleRead = async () => {
    const data = await getItemFromCollection("milestones", milestoneId, {
      fields: [
        "name",
        "start_date",
        "end_date",
        "description",
        "status",
        "billable_hours",
        {
          coordinator: [
            "id",
            "avatar",
            "first_name",
            "last_name",
            "user.role.name",
            "department.name",
            "title",
          ],
        },
      ],
    });

    if (data?.status) {
      setApiStatus(data.status);
    }

    return data;
  };

  return {
    form,
    requiredFields,
    handleCreate,
    handleRead,
    handleUpdate,
    handleClose,
    apiStatus,
  };
};
